import type {
  MonitorChannelMessage,
  SyncPreferences,
  ScrollSyncMessage,
  WLSyncMessage,
  CrosshairSyncMessage,
} from './types';
import { DEFAULT_SYNC_PREFERENCES } from './types';
import { MonitorChannel } from './MonitorChannel';
import type { ViewerSyncAdapter } from '../viewer/types';

// ---------------------------------------------------------------------------
// Types for the OHIF iframe bridge (postMessage protocol)
// ---------------------------------------------------------------------------

/**
 * Messages the SyncAdapter sends TO the OHIF iframe to apply remote state.
 * These extend the existing BRIT→OHIF postMessage protocol.
 */
export type SyncToOhifMessage =
  | { type: 'BRIT_SET_SCROLL'; seriesInstanceUID: string; imageIndex: number; worldPosition?: [number, number, number]; frameOfReferenceUID?: string }
  | { type: 'BRIT_SET_WL'; seriesInstanceUID: string; windowCenter: number; windowWidth: number; voiLutFunction?: string }
  | { type: 'BRIT_SET_TOOL'; toolName: string; toolOptions?: Record<string, unknown> }
  | { type: 'BRIT_SET_CROSSHAIR'; frameOfReferenceUID: string; worldPosition: [number, number, number] }
  | { type: 'BRIT_SET_ANNOTATION'; action: 'created' | 'updated' | 'deleted'; annotationUID: string; annotation?: unknown; seriesInstanceUID: string; sopInstanceUID: string }
  | { type: 'BRIT_SET_RESET'; scope: 'all' | 'viewport'; viewportId?: string; seriesInstanceUID?: string };

/**
 * Messages the OHIF iframe sends TO the parent (picked up by SyncAdapter).
 * These are new event notifications that OHIF emits on viewport changes.
 */
export type SyncFromOhifMessage =
  | { type: 'BRIT_SCROLL_CHANGED'; seriesInstanceUID: string; imageIndex: number; worldPosition?: [number, number, number]; frameOfReferenceUID?: string }
  | { type: 'BRIT_WL_CHANGED'; seriesInstanceUID: string; windowCenter: number; windowWidth: number; voiLutFunction?: string }
  | { type: 'BRIT_TOOL_CHANGED'; toolName: string; toolOptions?: Record<string, unknown> }
  | { type: 'BRIT_CROSSHAIR_MOVED'; frameOfReferenceUID: string; worldPosition: [number, number, number] }
  | { type: 'BRIT_ANNOTATION_CHANGED'; action: 'created' | 'updated' | 'deleted'; annotationUID: string; annotation?: unknown; seriesInstanceUID: string; sopInstanceUID: string }
  | { type: 'BRIT_RESET_TRIGGERED'; scope: 'all' | 'viewport'; viewportId?: string; seriesInstanceUID?: string };

// ---------------------------------------------------------------------------
// Throttle utility
// ---------------------------------------------------------------------------

function throttle<T extends unknown[]>(fn: (...args: T) => void, ms: number): (...args: T) => void {
  let lastCall = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: T) => {
    const now = Date.now();
    const remaining = ms - (now - lastCall);
    if (remaining <= 0) {
      if (timer) { clearTimeout(timer); timer = null; }
      lastCall = now;
      fn(...args);
    } else if (!timer) {
      timer = setTimeout(() => {
        lastCall = Date.now();
        timer = null;
        fn(...args);
      }, remaining);
    }
  };
}

// ---------------------------------------------------------------------------
// OHIFSyncAdapter
// ---------------------------------------------------------------------------

/**
 * Bridges OHIF iframe viewport events with the BroadcastChannel so that
 * scroll, W/L, tool, crosshair, and annotation changes propagate across
 * windows.
 *
 * Architecture:
 *   [ OHIF iframe ] ←postMessage→ [ OHIFSyncAdapter ] ←BroadcastChannel→ [ Other Windows ]
 *
 * Echo prevention: when applying a remote change to the local OHIF iframe,
 * the adapter sets a `muted` flag so the resulting local event isn't
 * re-broadcast.
 */
export class OHIFSyncAdapter implements ViewerSyncAdapter {
  private muted = false;
  private postMessageListener: ((event: MessageEvent) => void) | null = null;
  private channelUnsubscribe: (() => void) | null = null;
  private preferences: SyncPreferences = { ...DEFAULT_SYNC_PREFERENCES };

  // Throttled broadcast functions
  private broadcastScrollThrottled: (msg: ScrollSyncMessage) => void;
  private broadcastWLThrottled: (msg: WLSyncMessage) => void;
  private broadcastCrosshairThrottled: (msg: CrosshairSyncMessage) => void;

  channel: MonitorChannel;
  windowId: string;
  iframeRef: () => HTMLIFrameElement | null;

  constructor(
    channel: MonitorChannel,
    windowId: string,
    iframeRef: () => HTMLIFrameElement | null,
  ) {
    this.channel = channel;
    this.windowId = windowId;
    this.iframeRef = iframeRef;
    this.broadcastScrollThrottled = throttle((msg: ScrollSyncMessage) => {
      this.channel.send(msg);
    }, 16); // ~60fps

    this.broadcastWLThrottled = throttle((msg: WLSyncMessage) => {
      this.channel.send(msg);
    }, 100);

    this.broadcastCrosshairThrottled = throttle((msg: CrosshairSyncMessage) => {
      this.channel.send(msg);
    }, 16);
  }

  /** Update sync preferences (called from config UI or store). */
  setPreferences(prefs: SyncPreferences): void {
    this.preferences = { ...prefs };
  }

  /** Start listening for OHIF postMessages and BroadcastChannel messages. */
  start(): void {
    // Listen for OHIF iframe → parent postMessages
    this.postMessageListener = (event: MessageEvent) => {
      if (typeof event.data?.type !== 'string') return;
      this.handleOhifMessage(event.data as SyncFromOhifMessage);
    };
    window.addEventListener('message', this.postMessageListener);

    // Listen for BroadcastChannel → apply to local OHIF
    this.channelUnsubscribe = this.channel.subscribe((msg) => {
      this.handleChannelMessage(msg);
    });
  }

  /** Stop listening and clean up. */
  stop(): void {
    if (this.postMessageListener) {
      window.removeEventListener('message', this.postMessageListener);
      this.postMessageListener = null;
    }
    if (this.channelUnsubscribe) {
      this.channelUnsubscribe();
      this.channelUnsubscribe = null;
    }
  }

  // ── OHIF → BroadcastChannel (outbound) ─────────────────────────

  private handleOhifMessage(msg: SyncFromOhifMessage): void {
    if (this.muted) return;

    switch (msg.type) {
      case 'BRIT_SCROLL_CHANGED':
        if (!this.preferences.scroll) return;
        this.broadcastScrollThrottled({
          type: 'SCROLL_SYNC',
          sourceWindowId: this.windowId,
          seriesInstanceUID: msg.seriesInstanceUID,
          imageIndex: msg.imageIndex,
          worldPosition: msg.worldPosition,
          frameOfReferenceUID: msg.frameOfReferenceUID,
        });
        break;

      case 'BRIT_WL_CHANGED':
        if (!this.preferences.windowLevel) return;
        this.broadcastWLThrottled({
          type: 'WL_SYNC',
          sourceWindowId: this.windowId,
          seriesInstanceUID: msg.seriesInstanceUID,
          windowCenter: msg.windowCenter,
          windowWidth: msg.windowWidth,
          voiLutFunction: msg.voiLutFunction,
        });
        break;

      case 'BRIT_TOOL_CHANGED':
        if (!this.preferences.tool) return;
        this.channel.send({
          type: 'TOOL_SYNC',
          sourceWindowId: this.windowId,
          toolName: msg.toolName,
          toolOptions: msg.toolOptions,
        });
        break;

      case 'BRIT_CROSSHAIR_MOVED':
        if (!this.preferences.crosshair) return;
        this.broadcastCrosshairThrottled({
          type: 'CROSSHAIR_SYNC',
          sourceWindowId: this.windowId,
          frameOfReferenceUID: msg.frameOfReferenceUID,
          worldPosition: msg.worldPosition,
        });
        break;

      case 'BRIT_ANNOTATION_CHANGED':
        if (!this.preferences.annotations) return;
        this.channel.send({
          type: 'ANNOTATION_SYNC',
          sourceWindowId: this.windowId,
          action: msg.action,
          annotationUID: msg.annotationUID,
          annotation: msg.annotation,
          seriesInstanceUID: msg.seriesInstanceUID,
          sopInstanceUID: msg.sopInstanceUID,
        });
        break;

      case 'BRIT_RESET_TRIGGERED':
        this.channel.send({
          type: 'RESET_SYNC',
          sourceWindowId: this.windowId,
          scope: msg.scope,
          viewportId: msg.viewportId,
          seriesInstanceUID: msg.seriesInstanceUID,
        });
        break;
    }
  }

  // ── BroadcastChannel → OHIF (inbound) ──────────────────────────

  private handleChannelMessage(msg: MonitorChannelMessage): void {
    // Ignore messages from ourselves
    if ('sourceWindowId' in msg && msg.sourceWindowId === this.windowId) return;

    switch (msg.type) {
      case 'SCROLL_SYNC':
        if (!this.preferences.scroll) return;
        this.applyToOhif({
          type: 'BRIT_SET_SCROLL',
          seriesInstanceUID: msg.seriesInstanceUID,
          imageIndex: msg.imageIndex,
          worldPosition: msg.worldPosition,
          frameOfReferenceUID: msg.frameOfReferenceUID,
        });
        break;

      case 'WL_SYNC':
        if (!this.preferences.windowLevel) return;
        this.applyToOhif({
          type: 'BRIT_SET_WL',
          seriesInstanceUID: msg.seriesInstanceUID,
          windowCenter: msg.windowCenter,
          windowWidth: msg.windowWidth,
          voiLutFunction: msg.voiLutFunction,
        });
        break;

      case 'TOOL_SYNC':
        if (!this.preferences.tool) return;
        this.applyToOhif({
          type: 'BRIT_SET_TOOL',
          toolName: msg.toolName,
          toolOptions: msg.toolOptions,
        });
        break;

      case 'CROSSHAIR_SYNC':
        if (!this.preferences.crosshair) return;
        this.applyToOhif({
          type: 'BRIT_SET_CROSSHAIR',
          frameOfReferenceUID: msg.frameOfReferenceUID,
          worldPosition: msg.worldPosition,
        });
        break;

      case 'ANNOTATION_SYNC':
        if (!this.preferences.annotations) return;
        this.applyToOhif({
          type: 'BRIT_SET_ANNOTATION',
          action: msg.action,
          annotationUID: msg.annotationUID,
          annotation: msg.annotation,
          seriesInstanceUID: msg.seriesInstanceUID,
          sopInstanceUID: msg.sopInstanceUID,
        });
        break;

      case 'RESET_SYNC':
        this.applyToOhif({
          type: 'BRIT_SET_RESET',
          scope: msg.scope,
          viewportId: msg.viewportId,
          seriesInstanceUID: msg.seriesInstanceUID,
        });
        break;
    }
  }

  /**
   * Send a message to the OHIF iframe. Temporarily mutes the adapter so
   * the resulting local event isn't re-broadcast (echo prevention).
   */
  private applyToOhif(msg: SyncToOhifMessage): void {
    const iframe = this.iframeRef();
    if (!iframe?.contentWindow) return;

    this.muted = true;
    try {
      iframe.contentWindow.postMessage(msg, '*');
    } catch {
      // iframe may not be ready
    }
    // Un-mute after a frame so the local event fires and is ignored
    requestAnimationFrame(() => { this.muted = false; });
  }
}
